/* eslint-disable no-console */
/**
 * Cart context provider and hook
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
} from 'react'

import { useQuery } from '@tanstack/react-query'

import { useProfile } from '@/hooks/useProfile'

import { cartReducer } from './cart-reducer'
import {
  selectTotalQty,
  selectSubtotal,
  selectItemsArray,
  selectItemsMap,
  selectHasItem,
  selectItemQty,
  selectLineSubtotalById,
  selectPolicyIds,
  selectItemsByPolicy,
  groupItemsByPolicy,
  getCustomAddressSelector,
  isGreenpoolItemInPolicy,
  selectGreenpoolItemInPolicy,
  groupGreenpoolItemsByPolicy,
  selectGreenpoolItemsArray,
  selectPolicyIdsWithoutGreenpool,
  groupNonMandatoryItemsByPolicy,
  isNonGreenpoolItemInPolicy,
} from './cart-selectors'
import {
  readFromStorage,
  createInitialState,
  debouncedWriteToStorage,
  setupStorageListener,
} from './cart-storage'
import type {
  CartContextValue,
  Product,
  ProductId,
  PolicyId,
  CartItemPersisted,
  CartItem,
  PolicyTermsStatus,
  Asset,
} from './cart-types'
import { fetchPoliciesByIds } from './products-api'

const CartContext = createContext<CartContextValue | null>(null)

type CartProviderProps = {
  children: React.ReactNode
}

/**
 * Cart context provider component
 *
 * Manages cart state, persistence, product hydration, and policy hydration
 */
export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Initialize state from localStorage
  const [state, dispatch] = useReducer(cartReducer, null, () => {
    const persistedData = readFromStorage()
    return createInitialState(persistedData)
  })

  const { profile, isProfileLoading } = useProfile()

  // Get unique product and policy IDs for React Query
  const policyIds = useMemo(() => selectPolicyIds(state), [state])
  const products = useMemo(() => groupItemsByPolicy(state), [state])

  // Fetch policy details for hydration
  const { isLoading: isLoadingPolicies } = useQuery({
    queryKey: ['cart-policies', policyIds.sort().join(',')],
    queryFn: () => fetchPoliciesByIds(policyIds),
    enabled: policyIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 1,
    onSuccess(data) {
      if (!data) return

      const cartItems: CartItem[] = []

      Object.entries(products).forEach(([policyId, items]) => {
        items.forEach(item => {
          const policy = data[policyId]
          if (!policy) return

          const { id, name, products, greenpoolAssets } = policy

          let product
          let asset

          if (item.isGreenpoolProduct) {
            asset = greenpoolAssets?.find(a => a.id === item.productId)
            if (!asset) return
          } else {
            product = products?.find(p => p.id === item.productId && !p.isMandatory)
            if (!product) return
          }

          const productId = item.productId || product?.id || asset?.id
          if (!productId) return

          cartItems.push({
            policyId: item.policyId || id,
            productId,
            isGreenpoolProduct: item.isGreenpoolProduct,
            qty: item.qty,
            product,
            asset,
            policy: { id, name },
          })
        })
      })

      dispatch({ type: 'HYDRATE_ITEMS', payload: { items: cartItems } })
    },
  })

  const isHydrating = isLoadingPolicies

  // Persist to localStorage when state changes
  useEffect(() => {
    debouncedWriteToStorage(state)
  }, [state])

  // Setup multi-tab sync
  useEffect(() => {
    const handleStorageSync = (items: CartItemPersisted[]) => {
      dispatch({ type: 'SYNC_FROM_STORAGE', payload: { items } })
    }

    const cleanup = setupStorageListener(handleStorageSync)
    return cleanup
  }, [])

  // Clear cart when logged-in user changes (or login/logout happens)
  useEffect(() => {
    if (isProfileLoading) return

    const currentUserId = profile?.id ?? null

    // If the userId in state doesn't match the current profile, reset the cart
    if (state.userId !== currentUserId) {
      dispatch({ type: 'RESET_CART_FOR_USER', payload: { userId: currentUserId } })
    }
  }, [isProfileLoading, profile?.id, state.userId])

  // Actions
  const addItem = useCallback((policyId: PolicyId, product: Product, qty?: number) => {
    dispatch({ type: 'ADD_ITEM', payload: { policyId, product, qty } })
  }, [])

  const addItemsPerPolicy = useCallback((policyId: PolicyId, products: Product[]) => {
    products.forEach(product => {
      dispatch({ type: 'ADD_ITEM', payload: { policyId, product, qty: product.qty || 1 } })
    })
  }, [])

  const removeItem = useCallback((policyId: PolicyId, productId: ProductId) => {
    dispatch({ type: 'REMOVE_ITEM', payload: { policyId, productId } })
  }, [])

  const addGreenpoolItem = useCallback((policyId: PolicyId, asset: Asset) => {
    dispatch({ type: 'ADD_GREENPOOL_ITEM', payload: { policyId, asset } })
  }, [])

  const removeGreenpoolItem = useCallback((policyId: PolicyId, assetId: ProductId) => {
    dispatch({ type: 'REMOVE_GREENPOOL_ITEM', payload: { policyId, assetId } })
  }, [])

  const incrementQty = useCallback((policyId: PolicyId, productId: ProductId, delta?: number) => {
    dispatch({ type: 'INCREMENT_QTY', payload: { policyId, productId, delta } })
  }, [])

  const decrementQty = useCallback((policyId: PolicyId, productId: ProductId, delta?: number) => {
    dispatch({ type: 'DECREMENT_QTY', payload: { policyId, productId, delta } })
  }, [])

  const setQty = useCallback((policyId: PolicyId, productId: ProductId, qty: number) => {
    dispatch({ type: 'SET_QTY', payload: { policyId, productId, qty } })
  }, [])

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' })
  }, [])

  const clearPolicy = useCallback((policyId: PolicyId) => {
    dispatch({ type: 'CLEAR_POLICY', payload: { policyId } })
  }, [])

  const addPolicyTermsStatus = useCallback((policyId: PolicyId, status: PolicyTermsStatus) => {
    dispatch({ type: 'ADD_POLICY_TERMS_STATUS', payload: { policyId, status } })
  }, [])

  // Utilities
  const getLineSubtotal = useCallback(
    (policyId: PolicyId, productId: ProductId): number =>
      selectLineSubtotalById(state, policyId, productId),
    [state]
  )

  const hasProduct = useCallback(
    (policyId: PolicyId, productId: ProductId): boolean =>
      selectHasItem(state, policyId, productId),
    [state]
  )

  const qtyOf = useCallback(
    (policyId: PolicyId, productId: ProductId): number => selectItemQty(state, policyId, productId),
    [state]
  )

  const getItemsByPolicy = useCallback(
    (policyId: PolicyId) => selectItemsByPolicy({ state, policyId }),
    [state]
  )

  const getItemsByPolicyInOrder = useCallback(
    (policyId: PolicyId) => selectItemsByPolicy({ state, policyId, isSorted: false }),
    [state]
  )

  const getProductsByPolicy = useCallback(
    (policyId: PolicyId) =>
      selectItemsByPolicy({ state, policyId }).filter(
        item => !item.isGreenpoolProduct && !item.product?.isAccessory
      ),
    [state]
  )

  const getAccessoriesByPolicy = useCallback(
    (policyId: PolicyId) =>
      selectItemsByPolicy({ state, policyId }).filter(
        item => !item.isGreenpoolProduct && item.product?.isAccessory
      ),
    [state]
  )

  const getItemsGroupedByPolicy = useCallback(() => groupItemsByPolicy(state), [state])

  const getNonMandatoryItemsGroupedByPolicy = useCallback(
    () => groupNonMandatoryItemsByPolicy(state),
    [state]
  )

  const getPolicyIds = useCallback((): PolicyId[] => selectPolicyIds(state), [state])

  const getPolicyIdsWithoutGreenpool = useCallback(
    (): PolicyId[] => selectPolicyIdsWithoutGreenpool(state),
    [state]
  )

  const getItemInCart = useCallback(
    (productId: ProductId) => Object.values(state.items).find(item => item.productId === productId),
    [state]
  )

  const getPolicyTermsStatus = useCallback(
    (policyId: PolicyId) => state.policiesTermsStatus[policyId],
    [state]
  )

  const hasNonGreenpoolItem = useCallback(
    (policyId: PolicyId) => isNonGreenpoolItemInPolicy(state, policyId),
    [state]
  )

  const hasGreenpoolItem = useCallback(
    (policyId: PolicyId) => isGreenpoolItemInPolicy(state, policyId),
    [state]
  )

  const getGreenpoolItem = useCallback(
    (policyId: PolicyId) => selectGreenpoolItemInPolicy(state, policyId),
    [state]
  )

  const getGreenpoolItemsGroupedByPolicy = useCallback(
    () => groupGreenpoolItemsByPolicy(state),
    [state]
  )

  const setSelectedAddress = useCallback((addressId: number | undefined) => {
    dispatch({ type: 'SET_SELECTED_ADDRESS', payload: { addressId } })
  }, [])

  // Accept terms and conditions
  const setIsTermsAccepted = useCallback((accepted: boolean) => {
    dispatch({ type: 'SET_TERMS_ACCEPTED', payload: { accepted } })
  }, [])

  const clearConfirmData = useCallback(() => {
    dispatch({ type: 'CLEAR_CONFIRM_DATA' })
  }, [])

  const setCustomAddress = useCallback((address: string, zip: string, city: string) => {
    dispatch({ type: 'SET_CUSTOM_ADDRESS', payload: { address, zip, city } })
  }, [])

  const setIsPrivateAddress = useCallback((isPrivateAddress?: boolean) => {
    dispatch({ type: 'SET_IS_PRIVATE_ADDRESS', payload: { isPrivateAddress } })
  }, [])

  // Memoized derived state to prevent unnecessary re-renders
  const contextValue = useMemo(
    (): CartContextValue => ({
      // Derived state
      items: selectItemsArray(state),
      itemsMap: selectItemsMap(state),
      greenpoolItems: selectGreenpoolItemsArray(state),
      totalQty: selectTotalQty(state),
      subtotal: selectSubtotal(state),
      isHydrating,
      isTermsAccepted: state.isTermsAccepted,
      selectedAddressId: state.selectedAddressId,
      customAddress: getCustomAddressSelector(state),
      isPrivateAddress: state.isPrivateAddress,
      policiesTermsStatus: state.policiesTermsStatus,
      userId: state.userId,

      // Actions
      addItem,
      addItemsPerPolicy,
      removeItem,
      addGreenpoolItem,
      removeGreenpoolItem,
      incrementQty,
      decrementQty,
      setQty,
      clearCart,
      clearPolicy,
      setSelectedAddress,
      setIsTermsAccepted,
      clearConfirmData,
      setCustomAddress,
      setIsPrivateAddress,
      addPolicyTermsStatus,
      // Utilities
      getLineSubtotal,
      hasProduct,
      qtyOf,
      getItemsByPolicy,
      getProductsByPolicy,
      getAccessoriesByPolicy,
      getItemsByPolicyInOrder,
      getItemsGroupedByPolicy,
      getNonMandatoryItemsGroupedByPolicy,
      getPolicyIds,
      getPolicyIdsWithoutGreenpool,
      getItemInCart,
      getPolicyTermsStatus,
      hasNonGreenpoolItem,
      hasGreenpoolItem,
      getGreenpoolItem,
      getGreenpoolItemsGroupedByPolicy,
    }),
    [
      state,
      isHydrating,
      addItem,
      addItemsPerPolicy,
      removeItem,
      addGreenpoolItem,
      removeGreenpoolItem,
      incrementQty,
      decrementQty,
      setQty,
      clearCart,
      clearPolicy,
      getLineSubtotal,
      hasProduct,
      qtyOf,
      getItemsByPolicy,
      getProductsByPolicy,
      getAccessoriesByPolicy,
      getItemsByPolicyInOrder,
      getItemsGroupedByPolicy,
      getNonMandatoryItemsGroupedByPolicy,
      getPolicyIds,
      getPolicyIdsWithoutGreenpool,
      getItemInCart,
      setSelectedAddress,
      setIsTermsAccepted,
      clearConfirmData,
      setCustomAddress,
      setIsPrivateAddress,
      addPolicyTermsStatus,
      getPolicyTermsStatus,
      hasNonGreenpoolItem,
      hasGreenpoolItem,
      getGreenpoolItem,
      getGreenpoolItemsGroupedByPolicy,
    ]
  )

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
}

/**
 * Hook to access cart context
 *
 * @returns Cart context value with state and actions
 * @throws Error if used outside CartProvider
 */
export const useCart = (): CartContextValue => {
  const context = useContext(CartContext)

  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }

  return context
}
